/**
 * The user's spells on a Spellcasting Entry (issue #10): learning and forgetting spells, signature
 * spells, answering a spell Choice, preparing into slots, and hand-declared Entries. Pure
 * functions over the Character; legality is structural and read from the derived Entry view
 * (`SpellcastingEntryView`), as the guide reads its candidates from the sheet. Anything that is
 * merely off-list is allowed and warned about on the sheet, never blocked.
 */
import type { IndexRecord } from "../../shared/snapshot-format";
import type { SpellcastingEntryView, StatisticView } from "../engine";
import type { Character, KnownSpell, ManualSpellcastingEntry, SpellcastingState } from "./model";

export type EntryView = SpellcastingEntryView<StatisticView>;

export interface SpellChangeResult {
  character: Character;
  /** Why the change was refused (the Character is unchanged), or null. */
  blocked: string | null;
}

function stateOf(character: Character, id: string): SpellcastingState {
  return character.spellcasting.entries[id] ?? { known: [], prepared: {} };
}

function withState(character: Character, id: string, state: SpellcastingState): Character {
  return { ...character, spellcasting: { ...character.spellcasting, entries: { ...character.spellcasting.entries, [id]: state } } };
}

/** Facts about a spell from the catalogue index, as the pickers see them. */
export interface SpellRecordFacts {
  uuid: string;
  name: string;
  rank: number;
  cantrip: boolean;
  focus: boolean;
  traditions: string[];
  traits: string[];
  rarity: string;
  remaster: boolean;
}

export function spellRecordFacts(record: IndexRecord): SpellRecordFacts {
  const traditions = record.td ?? [];
  return {
    uuid: record.u,
    name: record.n,
    rank: Math.min(10, Math.max(1, record.l ?? 1)),
    cantrip: record.tr.includes("cantrip"),
    focus: record.tr.includes("focus") || (record.tr.includes("cantrip") && traditions.length === 0),
    traditions,
    traits: record.tr,
    rarity: record.r,
    remaster: record.rm,
  };
}

export interface SpellCandidateQuery {
  /** The Entry the spell is for. */
  entry: EntryView;
  /** For a slotted Entry: the rank group the spell joins (0 cantrips); null for focus and innate lists. */
  rank: number | null;
  /** A spell Choice's key, when answering one: its filter and candidates apply instead of the Entry's list. */
  choice?: string | null;
  text: string;
  /** Include Legacy Entries. */
  legacy: boolean;
  /** Include uncommon, rare and unique spells. */
  uncommon: boolean;
  limit: number;
}

/**
 * The spells a picker may offer: spell records of the Entry's tradition at a legal rank (a cantrip
 * for the cantrip group, a spell of the group's rank or lower otherwise), focus spells for a focus
 * Entry, anything of the tradition for an innate one, or a Choice's own candidates and filter.
 * Prefix matches on the name first, then substring matches, each in name order.
 */
export function spellCandidates(records: readonly IndexRecord[], q: SpellCandidateQuery): SpellRecordFacts[] {
  const needle = q.text.trim().toLowerCase();
  const choice = q.choice ? q.entry.choices.find((c) => c.key === q.choice) : undefined;
  const explicit = choice?.candidates ? new Set(choice.candidates) : null;
  const prefix: SpellRecordFacts[] = [];
  const contains: SpellRecordFacts[] = [];
  for (const record of records) {
    if (record.t !== "spell") continue;
    const facts = spellRecordFacts(record);
    if (explicit) {
      if (!explicit.has(record.u)) continue;
    } else if (choice) {
      const f = choice.filter;
      if (f.focus && !facts.focus) continue;
      if (!f.focus && facts.focus) continue;
      if (f.cantrip && !facts.cantrip) continue;
      if (!f.cantrip && f.rank !== null && f.rank > 0 && facts.cantrip) continue;
      if (f.rank !== null && f.rank > 0 && facts.rank > f.rank) continue;
      if (f.tradition && !facts.traditions.includes(f.tradition)) continue;
      if (f.trait && !facts.traits.includes(f.trait)) continue;
    } else if (q.entry.casting === "focus") {
      if (!facts.focus) continue;
    } else {
      if (facts.focus) continue;
      if (q.entry.tradition && !facts.traditions.includes(q.entry.tradition)) continue;
      if (q.rank === 0 && !facts.cantrip) continue;
      if (q.rank !== null && q.rank > 0 && (facts.cantrip || facts.rank > q.rank)) continue;
      if (q.entry.restrictedTo && !q.entry.restrictedTo.includes(record.u)) continue;
    }
    if (!q.legacy && !record.rm) continue;
    if (!q.uncommon && record.r !== "common" && !explicit) continue;
    if (needle) {
      const name = record.n.toLowerCase();
      if (name.startsWith(needle)) prefix.push(facts);
      else if (name.includes(needle)) contains.push(facts);
    } else prefix.push(facts);
  }
  const byName = (a: SpellRecordFacts, b: SpellRecordFacts): number => a.name.localeCompare(b.name);
  return [...prefix.sort(byName), ...contains.sort(byName)].slice(0, q.limit);
}

export interface LearnOptions {
  /** Spontaneous and innate Entries: the rank to know the spell at (its base rank when absent). */
  rank?: number;
  /** The spell Choice this answers. */
  choice?: string;
  level?: Character["level"];
}

/**
 * Learn a spell on an Entry. Structural refusals: a repertoire group that is full, a spell already
 * known at that rank, a Choice already answered, a Choice's filter or candidates not met.
 */
export function learnSpell(character: Character, entry: EntryView, facts: SpellRecordFacts, options: LearnOptions = {}): SpellChangeResult {
  const state = stateOf(character, entry.id);
  const rank = facts.cantrip ? undefined : options.rank !== undefined ? Math.max(facts.rank, options.rank) : undefined;
  const knownRank = facts.cantrip ? 0 : (rank ?? facts.rank);
  if (options.choice) {
    const choice = entry.choices.find((c) => c.key === options.choice);
    if (!choice) return { character, blocked: "That spell Choice is not open." };
    if (choice.answered) return { character, blocked: "That spell Choice is already answered; clear it first." };
    if (choice.candidates && !choice.candidates.includes(facts.uuid)) return { character, blocked: `${facts.name} is not one of the spells ${choice.granter.name} offers.` };
    if (!choice.candidates) {
      const f = choice.filter;
      if (f.focus !== facts.focus) return { character, blocked: f.focus ? `${facts.name} is not a focus spell.` : `${facts.name} is a focus spell.` };
      if (f.cantrip && !facts.cantrip) return { character, blocked: `${choice.granter.name} offers a cantrip.` };
      if (f.rank !== null && f.rank > 0 && facts.rank > f.rank) return { character, blocked: `${choice.granter.name} offers a spell of rank ${f.rank} or lower.` };
      if (f.tradition && !facts.traditions.includes(f.tradition)) return { character, blocked: `${facts.name} is not a ${f.tradition} spell.` };
      if (f.trait && !facts.traits.includes(f.trait)) return { character, blocked: `${facts.name} lacks the ${f.trait} trait.` };
    }
  } else {
    if (entry.spells.some((s) => s.uuid === facts.uuid && (entry.casting !== "spontaneous" || s.rank === knownRank || facts.cantrip))) {
      return { character, blocked: `${entry.name} already knows ${facts.name}${entry.casting === "spontaneous" && knownRank > 0 ? ` at rank ${knownRank}` : ""}.` };
    }
    if (entry.casting === "focus" && !facts.focus) return { character, blocked: `${facts.name} is not a focus spell.` };
    if (entry.casting !== "focus" && facts.focus) return { character, blocked: `${facts.name} is a focus spell; it belongs on a focus Entry.` };
    if (entry.casting === "spontaneous" && entry.limits) {
      const limit = entry.limits[String(knownRank)];
      if (!limit) return { character, blocked: `${entry.name} has no rank ${knownRank} slots to know ${facts.name} at.` };
      if (limit.used >= limit.max) return { character, blocked: `${entry.name} already knows ${limit.max} ${knownRank === 0 ? "cantrips" : `rank ${knownRank} spells`}.` };
    }
    if (entry.casting === "prepared" && entry.preparesFrom === "tradition") return { character, blocked: `${entry.name} prepares from the whole ${entry.traditionLabel.toLowerCase()} list; there is no spellbook to add to.` };
  }
  const duplicate = entry.spells.some((s) => s.uuid === facts.uuid && (entry.casting !== "spontaneous" || s.rank === knownRank || facts.cantrip));
  if (duplicate) return { character, blocked: `${entry.name} already knows ${facts.name}${entry.casting === "spontaneous" && knownRank > 0 ? ` at rank ${knownRank}` : ""}.` };
  const spell: KnownSpell = { uuid: facts.uuid };
  if (rank !== undefined && (entry.casting === "spontaneous" || entry.casting === "innate")) spell.rank = rank;
  if (options.choice) spell.choice = options.choice;
  if (options.level) spell.level = options.level;
  return { character: withState(character, entry.id, { ...state, known: [...state.known, spell] }), blocked: null };
}

/** Forget a spell the user learned (a granted spell cannot be forgotten). Its preparations are cleared. */
export function forgetSpell(character: Character, entryId: string, uuid: string, rank?: number): SpellChangeResult {
  const state = stateOf(character, entryId);
  const index = state.known.findIndex((k) => k.uuid === uuid && (rank === undefined || (k.rank ?? null) === rank));
  if (index < 0) return { character, blocked: "That spell was granted by an Entry and cannot be forgotten here." };
  const known = state.known.filter((_, i) => i !== index);
  const stillKnown = known.some((k) => k.uuid === uuid);
  const prepared = stillKnown ? state.prepared : Object.fromEntries(Object.entries(state.prepared).map(([r, list]) => [r, list.map((u) => (u === uuid ? null : u))]));
  return { character: withState(character, entryId, { known, prepared }), blocked: null };
}

/** Mark or unmark a spontaneous Entry's spell as a signature spell. Granted spells are marked by learning them again. */
export function setSignature(character: Character, entry: EntryView, uuid: string, signature: boolean): SpellChangeResult {
  if (entry.casting !== "spontaneous") return { character, blocked: "Only a spontaneous Entry has signature spells." };
  const state = stateOf(character, entry.id);
  const view = entry.spells.find((s) => s.uuid === uuid);
  if (!view) return { character, blocked: "That spell is not known here." };
  if (view.cantrip) return { character, blocked: "A cantrip cannot be a signature spell." };
  const index = state.known.findIndex((k) => k.uuid === uuid);
  let known: KnownSpell[];
  if (index >= 0) {
    known = state.known.map((k, i) => (i === index ? (signature ? { ...k, signature: true } : (({ signature: _s, ...rest }) => rest)(k)) : k));
  } else if (signature) {
    known = [...state.known, { uuid, rank: view.rank, signature: true }];
  } else return { character, blocked: null };
  return { character: withState(character, entry.id, { ...state, known }), blocked: null };
}

/**
 * Prepare a spell into a slot (null clears it). Structural refusals: a slot the Entry does not
 * have, a cantrip outside the cantrip group, a spell above the slot's rank, a spell a restricted
 * Entry may not hold. A spell not in the spellbook or off the list is allowed and warned about.
 */
export function prepareSpell(character: Character, entry: EntryView, rank: number, index: number, spell: SpellRecordFacts | null): SpellChangeResult {
  if (entry.casting !== "prepared") return { character, blocked: `${entry.name} does not prepare spells.` };
  if (entry.kind === "font") return { character, blocked: "The divine font's slots always hold its spell." };
  const count = entry.slots[String(rank)] ?? 0;
  if (index < 0 || index >= count) return { character, blocked: `${entry.name} has no rank ${rank} slot ${index + 1}.` };
  if (spell) {
    if (rank === 0 && !spell.cantrip) return { character, blocked: "Only a cantrip can be prepared in a cantrip slot." };
    if (rank > 0 && spell.cantrip) return { character, blocked: "A cantrip cannot be prepared in a ranked slot." };
    if (rank > 0 && spell.rank > rank) return { character, blocked: `${spell.name} is rank ${spell.rank}; the slot is rank ${rank}.` };
    if (spell.focus) return { character, blocked: "A focus spell is not prepared in a slot." };
    if (entry.restrictedTo && !entry.restrictedTo.includes(spell.uuid)) return { character, blocked: `${entry.name} may only hold its own spells.` };
  }
  const state = stateOf(character, entry.id);
  const list = [...(state.prepared[String(rank)] ?? [])];
  list.length = count;
  for (let i = 0; i < count; i++) list[i] ??= null;
  list[index] = spell?.uuid ?? null;
  return { character: withState(character, entry.id, { ...state, prepared: { ...state.prepared, [String(rank)]: list } }), blocked: null };
}

/** Add a hand-declared Spellcasting Entry with the next free id. */
export function addManualEntry(character: Character, entry: Omit<ManualSpellcastingEntry, "id">): Character {
  const used = new Set(character.spellcasting.manual.map((m) => m.id));
  let n = 1;
  while (used.has(`manual:${n}`)) n++;
  return { ...character, spellcasting: { ...character.spellcasting, manual: [...character.spellcasting.manual, { ...entry, id: `manual:${n}` }] } };
}

export function updateManualEntry(character: Character, id: string, change: Partial<Omit<ManualSpellcastingEntry, "id">>): Character {
  return { ...character, spellcasting: { ...character.spellcasting, manual: character.spellcasting.manual.map((m) => (m.id === id ? { ...m, ...change } : m)) } };
}

/** Remove a hand-declared Entry with the spells stored on it. */
export function removeManualEntry(character: Character, id: string): Character {
  const entries = { ...character.spellcasting.entries };
  delete entries[id];
  return { ...character, spellcasting: { entries, manual: character.spellcasting.manual.filter((m) => m.id !== id) } };
}

/** Drop stored state of Entries the sheet no longer derives (a dedication retrained away), keeping manual ones. */
export function pruneSpellcasting(character: Character, liveIds: Iterable<string>): Character {
  const live = new Set(liveIds);
  const entries: Character["spellcasting"]["entries"] = {};
  let changed = false;
  for (const [id, state] of Object.entries(character.spellcasting.entries)) {
    if (live.has(id) || id.startsWith("manual:")) entries[id] = state;
    else changed = true;
  }
  return changed ? { ...character, spellcasting: { ...character.spellcasting, entries } } : character;
}
